// Inbox › Messages (gap #3): the company's texts and emails, newest first, with the ones that
// couldn't be sent easy to find. Words for a message's purpose and status, the filters, and who may
// be written to. Customer replies are in the list too (direction 'in'), bold until someone opens them.

import type { ContactMethod, Customer } from '../types.ts';
import { phoneText } from '../types.ts';

export type MessageStatus =
  | 'queued'
  | 'sending'
  | 'sent'
  | 'delivered'
  | 'failed'
  | 'undelivered'
  /** a customer's reply */
  | 'received'
  /** held while the company mirrors ServiceTitan: never sent, waiting for a person to release it */
  | 'held_mirror';

/** One message as messages.list / messages.get give it. */
export interface Message {
  id: string;
  customerId: string | null;
  customerName: string | null;
  channel: 'sms' | 'email';
  purpose: string;
  to: string;
  subject: string | null;
  body: string;
  status: MessageStatus;
  errorCode: string | null;
  errorMessage: string | null;
  jobId: string | null;
  queuedAt: string;
  sentAt: string | null;
  deliveredAt: string | null;
  /** 'in' is a customer's reply; absent is one the company sent. */
  direction?: 'in' | 'out';
  /** A reply's sender (a phone or an email). */
  from?: string | null;
  /** A reply: when someone in the office opened it; null while it's new. */
  readAt?: string | null;
  /** A reply: when it came in. */
  receivedAt?: string | null;
  /** A failed one sent again (messages.retry): the copy that went in its place. */
  retriedBy?: string | null;
  /** That copy: the failed one it replaced. */
  retryOf?: string | null;
}

/** The filter's choices, and what each asks messages.list for. */
export const STATUS_FILTERS = [
  { key: 'all', label: 'All', query: {} },
  { key: 'replies', label: 'Replies', query: { direction: 'in' } },
  { key: 'failed', label: 'Couldn’t send', query: { status: ['failed', 'undelivered'] } },
  { key: 'waiting', label: 'Waiting to go', query: { status: ['queued', 'sending'] } },
  { key: 'held', label: 'Held (mirroring)', query: { status: ['held_mirror'] } },
  { key: 'sent', label: 'Sent', query: { status: ['sent', 'delivered'] } },
] as const satisfies readonly {
  key: string;
  label: string;
  query: { direction?: 'in' | 'out'; status?: readonly MessageStatus[] };
}[];
export type StatusFilter = (typeof STATUS_FILTERS)[number]['key'];

/**
 * The filters to offer: "Held (mirroring)" only while the company has a held message (or it's the
 * one chosen), so a company that isn't mirroring ServiceTitan sees the usual five (st-mirror §3.5).
 */
export const visibleFilters = (held: number | null, current: StatusFilter) =>
  STATUS_FILTERS.filter((f) => f.key !== 'held' || (held ?? 0) > 0 || current === 'held');

export const isFailed = (m: Pick<Message, 'status'>) => m.status === 'failed' || m.status === 'undelivered';
/** Failed and not yet sent again: what "Couldn't send" counts and tints. */
export const isUnsent = (m: Pick<Message, 'status' | 'retriedBy'>) => isFailed(m) && !m.retriedBy;
export const isReply = (m: Pick<Message, 'direction'>) => m.direction === 'in';
/** A reply no one in the office has opened yet. */
export const isUnread = (m: Pick<Message, 'direction' | 'readAt'>) => m.direction === 'in' && !m.readAt;

/** The chip on a message: what happened to it, in words, and its tone. */
export function statusChip(m: Pick<Message, 'status' | 'direction' | 'readAt' | 'retriedBy'>): {
  text: string;
  tone: 'ok' | 'warn' | 'bad' | 'brand' | '';
} {
  if (m.direction === 'in')
    return m.readAt ? { text: 'Reply', tone: '' } : { text: 'New reply', tone: 'brand' };
  if (m.retriedBy) return { text: 'Sent again', tone: '' };
  switch (m.status) {
    case 'queued':
    case 'sending':
      return { text: 'Waiting to go', tone: '' };
    case 'sent':
      return { text: 'Sent', tone: '' };
    case 'delivered':
      return { text: 'Delivered', tone: 'ok' };
    case 'undelivered':
      return { text: 'Not delivered', tone: 'bad' };
    case 'held_mirror':
      return { text: 'Held while mirroring ServiceTitan', tone: 'warn' };
    default:
      return { text: 'Couldn’t send', tone: 'bad' };
  }
}

const PURPOSE: Record<string, string> = {
  booking_confirmation: 'Booking confirmation',
  appointment_reminder: 'Reminder',
  on_the_way: 'On the way',
  estimate_follow_up: 'Estimate',
  invoice: 'Invoice',
  payment_reminder: 'Payment reminder',
  review_request: 'Rating request',
  general: 'Note',
  manual: 'Note',
  pay_link: 'Pay link',
};
/** "Reminder", "Invoice", "Note": what a message was for. */
export const purposeWords = (p: string): string =>
  PURPOSE[p] ?? (p.charAt(0).toUpperCase() + p.slice(1)).replace(/_/g, ' ');

/** What a text in was: STOP and START change whether we may text them; anything else is a reply. */
export const replyWords = (m: Pick<Message, 'purpose'>): string =>
  m.purpose === 'stop'
    ? 'Asked to stop texts'
    : m.purpose === 'start'
      ? 'Asked for texts again'
      : 'Their reply';

/** A message's words; a text in can come with none (a picture alone). */
export const bodyText = (m: Pick<Message, 'body'>): string => m.body.trim() || 'No words in this text.';

/** Where it went, as a person reads it: "(604) 555-0101" or the email address. */
export const toText = (m: Pick<Message, 'channel' | 'to'>): string =>
  m.channel === 'sms' ? phoneText(m.to) : m.to;

/** The customer's end of it: where one we sent went, or where a reply came from. */
export const theirAddress = (m: Pick<Message, 'channel' | 'to' | 'direction' | 'from'>): string =>
  m.direction === 'in' && m.from ? toText({ channel: m.channel, to: m.from }) : toText(m);

/** "Text to (604) 555-0101" or "Email from pat@example.com". */
export const channelLine = (m: Pick<Message, 'channel' | 'to' | 'direction' | 'from'>): string =>
  `${m.channel === 'sms' ? 'Text' : 'Email'} ${m.direction === 'in' ? 'from' : 'to'} ${theirAddress(m)}`;

/** The first line of a message, for the list (the whole of it is in the detail). */
export function firstLine(body: string, max = 90): string {
  const line = body.split(/\r?\n/).find((l) => l.trim()) ?? '';
  return line.length > max ? `${line.slice(0, max - 1).trimEnd()}…` : line;
}

/** When a message happened: a reply when it came in, one we sent when it was written. */
export const messageTime = (m: Pick<Message, 'queuedAt' | 'receivedAt'>): string =>
  m.receivedAt ?? m.queuedAt;

/** "9:42 a.m." today, "Tue 9:42 a.m." this week, else "Oct 3". */
export function whenText(iso: string, timeZone: string, now = new Date()): string {
  const d = new Date(iso);
  const day = (x: Date) => new Intl.DateTimeFormat('en-CA', { timeZone, dateStyle: 'short' }).format(x);
  const time = new Intl.DateTimeFormat('en-CA', { timeZone, hour: 'numeric', minute: '2-digit' }).format(d);
  if (day(d) === day(now)) return time;
  if (now.getTime() - d.getTime() < 6 * 86_400_000)
    return `${new Intl.DateTimeFormat('en-CA', { timeZone, weekday: 'short' }).format(d)} ${time}`;
  return new Intl.DateTimeFormat('en-CA', { timeZone, month: 'short', day: 'numeric' }).format(d);
}

/** "Oct 15, 2026, 9:42 a.m.": a full moment, for the detail's timeline. */
export const momentText = (iso: string, timeZone: string): string =>
  new Intl.DateTimeFormat('en-CA', { timeZone, dateStyle: 'medium', timeStyle: 'short' }).format(
    new Date(iso),
  );

/** A failed message's error, in words a person can act on: the api's own when it has them. */
export function errorWords(m: Pick<Message, 'errorCode' | 'errorMessage'>): string {
  if (m.errorCode === 'send_state_unknown')
    return 'The send was interrupted, so it may or may not have gone out. Check with the customer before sending it again.';
  if (m.errorMessage) return m.errorMessage;
  switch (m.errorCode) {
    case 'do_not_contact':
      return 'The customer asked not to be contacted, so this was not sent.';
    case 'consent_missing':
      return 'The customer has not agreed to messages at this address, so this was not sent.';
    case 'no_provider':
      return 'Texting isn’t set up for your company yet.';
    case null:
    case '':
      return 'The provider couldn’t deliver it.';
    default:
      return `The provider couldn’t deliver it (${m.errorCode}).`;
  }
}

/** Each way to reach a customer, with whether it may be written to now and the words why not. */
export interface Reachable {
  method: ContactMethod;
  contactName: string;
  /** The phone or email as a person reads it. */
  address: string;
  label: string;
  chip: { text: string; tone: 'ok' | 'warn' | '' };
  sendable: boolean;
}
export function reachable(c: Customer): Reachable[] {
  return c.contacts.flatMap((k) =>
    k.methods.map((m) => {
      const address = m.kind === 'phone' ? phoneText(m.value) : m.value;
      const label = `${k.name} · ${address}`;
      const what = m.kind === 'phone' ? 'texts' : 'email';
      const chip: Reachable['chip'] =
        c.doNotContact || m.doNotContact
          ? { text: 'Do not contact', tone: 'warn' }
          : m.consent
            ? { text: m.kind === 'phone' ? 'Texts OK' : 'Email OK', tone: 'ok' }
            : { text: `No ${what} consent`, tone: '' };
      return {
        method: m,
        contactName: k.name,
        address,
        label,
        chip,
        sendable: !c.doNotContact && !m.doNotContact && m.consent,
      };
    }),
  );
}

/** The live events that change the log: one queued, sent, delivered, failed or received; replies read. */
export const MESSAGE_EVENTS = /^(message\.[a-z_]+|messages\.read)$/;

/**
 * A fresh first page over what's shown, kept in place: what's newer replaces the top, and any older
 * pages already opened ("Show older messages") stay below it. Ids are time-ordered, newest first.
 */
export function mergeFirstPage(shown: readonly Message[] | null, page: readonly Message[]): Message[] {
  const last = page[page.length - 1];
  if (!shown || !last) return [...page];
  const fresh = new Set(page.map((m) => m.id));
  return [...page, ...shown.filter((m) => !fresh.has(m.id) && m.id < last.id)];
}

/** The longest message the api takes (messages.body is 1 to 1,600 characters). */
export const BODY_MAX = 1600;
